import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { CreditCard, FileText, CheckCircle, Clock, AlertCircle, Link2Off } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function Billing() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [linked, setLinked] = useState(false);
  const [contactName, setContactName] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/invoices`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setLinked(data.linked || false);
        setContactName(data.contact_name || '');
        setMessage(data.message || '');
      } else {
        toast.error('Failed to load billing data');
      }
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'PAID': { label: 'Paid', icon: CheckCircle, color: 'bg-primary/10 text-primary border-primary/20' },
      'AUTHORISED': { label: 'Pending', icon: Clock, color: 'bg-accent/10 text-accent border-accent/20' },
      'SUBMITTED': { label: 'Submitted', icon: Clock, color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      'DRAFT': { label: 'Draft', icon: FileText, color: 'bg-muted/10 text-muted-foreground border-muted/20' },
      'VOIDED': { label: 'Voided', icon: AlertCircle, color: 'bg-destructive/10 text-destructive border-destructive/20' },
      'DELETED': { label: 'Deleted', icon: AlertCircle, color: 'bg-destructive/10 text-destructive border-destructive/20' },
    };
    return statusMap[status] || { label: status, icon: FileText, color: 'bg-muted/10 text-muted-foreground border-muted/20' };
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', EUR: '€', AUD: 'A$' };
    const symbol = symbols[currency] || currency + ' ';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Xero dates come as /Date(timestamp)/ format
    const match = dateStr.match(/\/Date\((\d+)/);
    if (match) {
      return new Date(parseInt(match[1])).toLocaleDateString('en-GB');
    }
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  // Calculate summary from Xero invoices
  const summary = {
    totalInvoices: invoices.length,
    paidInvoices: invoices.filter(i => i.Status === 'PAID').length,
    pendingInvoices: invoices.filter(i => ['AUTHORISED', 'SUBMITTED'].includes(i.Status)).length,
    totalPaid: invoices.filter(i => i.Status === 'PAID').reduce((sum, i) => sum + (i.Total || 0), 0),
    totalOutstanding: invoices.filter(i => i.Status === 'AUTHORISED').reduce((sum, i) => sum + (i.AmountDue || 0), 0),
  };

  if (loading) {
    return (
      <DashboardLayout title="Billing">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!linked) {
    return (
      <DashboardLayout title="Billing">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Link2Off className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-mono font-bold mb-2">Billing Not Connected</h3>
          <p className="text-muted-foreground max-w-md">
            {message || 'Your account has not been linked to our billing system yet. Please contact your account administrator.'}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Billing">
      <div className="space-y-6" data-testid="billing-page">
        {/* Linked Contact Info */}
        {contactName && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              Billing for: <span className="text-primary font-bold">{contactName}</span>
            </p>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-primary" data-testid="total-paid-card">
            <div className="flex items-center justify-between mb-2">
              <CreditCard className="h-8 w-8 text-primary" />
            </div>
            <p className="text-3xl font-mono font-bold" data-testid="total-paid-amount">
              {formatCurrency(summary.totalPaid)}
            </p>
            <p className="text-sm text-muted-foreground font-mono">Total Paid</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-accent">
            <div className="flex items-center justify-between mb-2">
              <AlertCircle className="h-8 w-8 text-accent" />
            </div>
            <p className="text-3xl font-mono font-bold">{formatCurrency(summary.totalOutstanding)}</p>
            <p className="text-sm text-muted-foreground font-mono">Outstanding</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-primary">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="h-8 w-8 text-primary" />
            </div>
            <p className="text-3xl font-mono font-bold">{summary.paidInvoices}</p>
            <p className="text-sm text-muted-foreground font-mono">Paid Invoices</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 border-t-2 border-t-accent">
            <div className="flex items-center justify-between mb-2">
              <Clock className="h-8 w-8 text-accent" />
            </div>
            <p className="text-3xl font-mono font-bold">{summary.pendingInvoices}</p>
            <p className="text-sm text-muted-foreground font-mono">Pending Invoices</p>
          </div>
        </div>

        {/* Invoices List */}
        <div className="bg-card border border-border rounded-lg p-6" data-testid="invoices-section">
          <h2 className="text-xl font-mono font-bold mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Your Invoices
          </h2>

          {invoices.length === 0 ? (
            <div className="text-center py-12" data-testid="no-invoices-message">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-mono font-bold mb-2">No Invoices Yet</h3>
              <p className="text-muted-foreground">Invoices will appear here once generated</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => {
                const statusInfo = getStatusDisplay(invoice.Status);
                const StatusIcon = statusInfo.icon;
                return (
                  <div 
                    key={invoice.InvoiceID} 
                    className="flex items-center justify-between p-4 bg-background rounded-md border border-border hover:border-primary/50 transition-colors"
                    data-testid={`invoice-item-${invoice.InvoiceNumber}`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <StatusIcon className={`h-5 w-5 ${statusInfo.color.split(' ')[1]}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <p className="font-mono font-bold" data-testid={`invoice-number-${invoice.InvoiceNumber}`}>
                            {invoice.InvoiceNumber}
                          </p>
                          <span className={`px-3 py-1 rounded-full text-xs font-mono border ${statusInfo.color}`}>
                            {statusInfo.label.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                          <span className="font-mono">
                            Due: {formatDate(invoice.DueDateString || invoice.DueDate)}
                          </span>
                          {invoice.Reference && (
                            <span className="font-mono">Ref: {invoice.Reference}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-2xl font-mono font-bold" data-testid={`invoice-amount-${invoice.InvoiceNumber}`}>
                        {formatCurrency(invoice.Total, invoice.CurrencyCode)}
                      </p>
                      {invoice.Status === 'AUTHORISED' && invoice.AmountDue > 0 && (
                        <p className="text-xs text-accent font-mono">
                          Due: {formatCurrency(invoice.AmountDue, invoice.CurrencyCode)}
                        </p>
                      )}
                      {invoice.Status === 'PAID' && invoice.FullyPaidOnDate && (
                        <p className="text-xs text-muted-foreground font-mono">
                          Paid: {formatDate(invoice.FullyPaidOnDate)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Payment Information */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-mono font-bold mb-4">Payment Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-mono font-bold text-muted-foreground mb-2">Next Payment Due</p>
              <p className="text-lg font-mono">
                {invoices.find(i => i.Status === 'AUTHORISED') 
                  ? formatDate(invoices.find(i => i.Status === 'AUTHORISED').DueDateString || invoices.find(i => i.Status === 'AUTHORISED').DueDate)
                  : 'No pending payments'}
              </p>
            </div>
            <div>
              <p className="text-sm font-mono font-bold text-muted-foreground mb-2">Payment Method</p>
              <p className="text-lg font-mono">Bank Transfer / Direct Debit</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Billing;
